import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

export type InventoryReconciliationKind =
  | "inventory_adjustment"
  | "purchase_order_receive"
  | "inventory_transfer_ship"
  | "inventory_transfer_receive"
  | "stocktake_apply"
  | "stocktake_apply_chunk";

export type InventoryReconciliationDecision = "applied" | "not_applied";

export type JsonValue =
  string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export interface InventoryReconciliationOperation {
  id: string;
  kind: InventoryReconciliationKind;
  status: string;
  resource_id: string | null;
  result_resource_id: string | null;
  idempotency_key: string;
  idempotency_started_at: string | null;
  request_payload: Record<string, unknown> | null;
  external_payload: Record<string, unknown> | null;
  reconciliation_decision: InventoryReconciliationDecision | null;
  reconciliation_actor: string | null;
  reconciled_at: string | null;
  reconciliation_note: string | null;
  reconciliation_evidence: Record<string, unknown> | null;
  requires_inventory_sync: boolean;
  authoritative_sync_path: string | null;
  created_at: string;
  updated_at: string;
}

export interface InventoryReconcileInput {
  decision: InventoryReconciliationDecision;
  confirmed: true;
  note: string;
  evidence: Record<string, JsonValue>;
}

interface InventoryReconciliationListResponse {
  operations: InventoryReconciliationOperation[];
}

export const inventoryReconciliationKeys = {
  all: ["inventory-reconciliations"] as const,
  unresolved: ["inventory-reconciliations", "unresolved"] as const,
  detail: (operationId: StockyPublicBigIntId) =>
    [
      "inventory-reconciliations",
      "detail",
      canonicalStockyPublicBigIntId(operationId),
    ] as const,
};

function normalizeInventoryReconciliationOperation(
  value: unknown,
): InventoryReconciliationOperation {
  if (value === null || typeof value !== "object") {
    throw new TypeError("inventory reconciliation response is invalid");
  }
  const operation = value as Omit<
    InventoryReconciliationOperation,
    "id" | "resource_id" | "result_resource_id"
  > & {
    id: unknown;
    resource_id: unknown;
    result_resource_id: unknown;
  };
  return {
    ...operation,
    id: canonicalStockyPublicBigIntId(operation.id),
    resource_id:
      operation.resource_id == null
        ? null
        : canonicalStockyPublicBigIntId(operation.resource_id),
    result_resource_id:
      operation.result_resource_id == null
        ? null
        : canonicalStockyPublicBigIntId(operation.result_resource_id),
  };
}

export async function listInventoryReconciliations(
  includeReconciled = false,
): Promise<InventoryReconciliationListResponse> {
  const response = await apiFetch<{ operations: unknown[] }>(
    "/api/inventory-operations/reconciliation",
    {
      query: includeReconciled ? { include_reconciled: "true" } : undefined,
    },
  );
  if (!Array.isArray(response.operations)) {
    throw new TypeError("inventory reconciliation list response is invalid");
  }
  return {
    operations: response.operations.map(
      normalizeInventoryReconciliationOperation,
    ),
  };
}

export async function getInventoryReconciliation(
  operationId: StockyPublicBigIntId,
): Promise<InventoryReconciliationOperation> {
  const canonicalId = canonicalStockyPublicBigIntId(operationId);
  return normalizeInventoryReconciliationOperation(
    await apiFetch(
      `/api/inventory-operations/reconciliation/${encodeURIComponent(canonicalId)}`,
    ),
  );
}

export async function reconcileInventoryOperation(
  operationId: StockyPublicBigIntId,
  input: InventoryReconcileInput,
): Promise<InventoryReconciliationOperation> {
  const canonicalId = canonicalStockyPublicBigIntId(operationId);
  return normalizeInventoryReconciliationOperation(
    await apiFetch(
      `/api/inventory-operations/reconciliation/${encodeURIComponent(canonicalId)}/reconcile`,
      { method: "POST", body: input },
    ),
  );
}
